/**
 * @module @liquidefy/code-editor/terminal/welcome/digest
 * @version 1.0.0
 * @license LQD Research LLC
 * @rust digest.rs — false; 4/2/26; sm
 *
 * Output digestion and simplification engine.
 * Transforms raw terminal output into human-readable summary cards.
 * Parses test results, build output, git status, install logs,
 * and errors into structured, scannable digests.
 * All functions pure. No DOM, no side effects.
 */


// ═══ §1 TYPES ═══

type DigestType =
  | 'test-results'
  | 'build-result'
  | 'install-result'
  | 'error-summary'
  | 'git-status'
  | 'git-diff'
  | 'git-log'
  | 'file-listing'
  | 'process-list'
  | 'disk-usage'
  | 'deploy-result'
  | 'general-success'
  | 'general-error'
  | 'raw-output';

interface DigestDetail {
  readonly label: string;
  readonly value: string | number;
  readonly type: 'text' | 'number' | 'badge' | 'list' | 'link';
  readonly status?: 'success' | 'warning' | 'error' | 'neutral';
}

interface OutputDigest {
  readonly type: DigestType;
  readonly headline: string;
  readonly status: 'success' | 'warning' | 'error' | 'info' | 'neutral';
  readonly details: DigestDetail[];
  readonly rawOutput: string;
  readonly duration?: number;
  readonly actionSuggestion?: string;
  readonly showRaw: boolean;
}

interface DigestState {
  readonly enabled: boolean;
  readonly mode: 'always' | 'auto' | 'never';
  readonly digests: OutputDigest[];
  readonly showRawByDefault: boolean;
}

export type {
  DigestType,
  DigestDetail,
  OutputDigest,
  DigestState,
};


// ═══ §2 STATE MANAGEMENT ═══

function createDigestState(enabled: boolean = true): DigestState {
  return {
    enabled,
    mode: 'auto',
    digests: [],
    showRawByDefault: false,
  };
}

function enableDigest(state: DigestState): DigestState {
  return { ...state, enabled: true };
}

function disableDigest(state: DigestState): DigestState {
  return { ...state, enabled: false };
}

function setDigestMode(state: DigestState, mode: DigestState['mode']): DigestState {
  return { ...state, mode };
}

function addDigest(state: DigestState, digest: OutputDigest): DigestState {
  return { ...state, digests: [...state.digests, digest] };
}

function clearDigests(state: DigestState): DigestState {
  return { ...state, digests: [] };
}

function toggleRaw(state: DigestState, index: number): DigestState {
  if (index < 0 || index >= state.digests.length) return state;
  const digests = state.digests.map((d, i) =>
    i === index ? { ...d, showRaw: !d.showRaw } : d
  );
  return { ...state, digests };
}

function getDigests(state: DigestState): OutputDigest[] {
  return state.digests;
}


// ═══ §3 DURATION EXTRACTION ═══

function extractDuration(output: string): number | undefined {
  // Pattern: "Time: 1.234s" or "in 1.23s" or "took 1234ms" or "1.23 seconds" or "Duration: 1.23s"
  const secondsMatch = output.match(/(?:Time|Duration|Finished|Completed|Done|took|in)\s*[:=]?\s*([\d.]+)\s*s(?:ec(?:ond)?s?)?/i);
  if (secondsMatch) return parseFloat(secondsMatch[1]!) * 1000;

  const msMatch = output.match(/(?:Time|Duration|Finished|Completed|Done|took|in)\s*[:=]?\s*([\d.]+)\s*ms/i);
  if (msMatch) return parseFloat(msMatch[1]!);

  // "1m 23s" or "1:23"
  const minSecMatch = output.match(/(\d+)\s*m\s*(\d+(?:\.\d+)?)\s*s/i);
  if (minSecMatch) return (parseInt(minSecMatch[1]!, 10) * 60 + parseFloat(minSecMatch[2]!)) * 1000;

  return undefined;
}


// ═══ §4 TEST OUTPUT DIGESTION ═══

function digestTestOutput(output: string, framework?: string): OutputDigest {
  const lines = output.split('\n');
  let passed = 0, failed = 0, skipped = 0, total = 0;
  const failureDetails: string[] = [];
  const duration = extractDuration(output);

  // Jest / Vitest patterns
  const jestSummary = output.match(/Tests:\s+(\d+)\s+failed.*?(\d+)\s+passed.*?(\d+)\s+total/i)
    ?? output.match(/Tests:\s+(\d+)\s+passed.*?(\d+)\s+total/i);

  if (jestSummary) {
    if (jestSummary.length === 4) {
      failed = parseInt(jestSummary[1]!, 10);
      passed = parseInt(jestSummary[2]!, 10);
      total = parseInt(jestSummary[3]!, 10);
    } else {
      passed = parseInt(jestSummary[1]!, 10);
      total = parseInt(jestSummary[2]!, 10);
    }
    skipped = total - passed - failed;
  }

  // pytest pattern: "5 passed, 2 failed, 1 skipped"
  if (total === 0) {
    const pyPassed = output.match(/(\d+)\s+passed/i);
    const pyFailed = output.match(/(\d+)\s+failed/i);
    const pySkipped = output.match(/(\d+)\s+skipped/i);
    if (pyPassed || pyFailed) {
      passed = pyPassed ? parseInt(pyPassed[1]!, 10) : 0;
      failed = pyFailed ? parseInt(pyFailed[1]!, 10) : 0;
      skipped = pySkipped ? parseInt(pySkipped[1]!, 10) : 0;
      total = passed + failed + skipped;
    }
  }

  // cargo test: "test result: ok. X passed; Y failed; Z ignored"
  if (total === 0) {
    const cargoMatch = output.match(/test result:.*?(\d+)\s+passed;\s*(\d+)\s+failed;\s*(\d+)\s+ignored/i);
    if (cargoMatch) {
      passed = parseInt(cargoMatch[1]!, 10);
      failed = parseInt(cargoMatch[2]!, 10);
      skipped = parseInt(cargoMatch[3]!, 10);
      total = passed + failed + skipped;
    }
  }

  // go test: "ok" or "FAIL" lines, count individual "--- PASS" / "--- FAIL"
  if (total === 0) {
    const goPassed = (output.match(/--- PASS/g) ?? []).length;
    const goFailed = (output.match(/--- FAIL/g) ?? []).length;
    if (goPassed + goFailed > 0) {
      passed = goPassed;
      failed = goFailed;
      total = passed + failed;
    }
  }

  // Fallback: count checkmarks and X marks
  if (total === 0) {
    for (const line of lines) {
      if (/[✓✔√]|PASS/i.test(line)) passed++;
      else if (/[✗✘×]|FAIL/i.test(line)) failed++;
      else if (/skip|pending|todo/i.test(line)) skipped++;
    }
    total = passed + failed + skipped;
  }

  // Collect failure details (lines with FAIL or ✗ or error indications)
  let inFailure = false;
  for (const line of lines) {
    if (/FAIL|✗|✘|×|Error:|AssertionError|Expected.*Received/i.test(line)) {
      inFailure = true;
      failureDetails.push(line.trim());
    } else if (inFailure && line.trim() === '') {
      inFailure = false;
    } else if (inFailure && failureDetails.length < 10) {
      failureDetails.push(line.trim());
    }
  }

  const status = failed > 0 ? 'error' : (skipped > 0 ? 'warning' : 'success');
  const headline = failed > 0
    ? `${failed} test${failed === 1 ? '' : 's'} failed out of ${total}`
    : `All ${total} test${total === 1 ? '' : 's'} passed`;

  const details: DigestDetail[] = [
    { label: 'Passed', value: passed, type: 'number', status: 'success' },
    { label: 'Failed', value: failed, type: 'number', status: failed > 0 ? 'error' : 'neutral' },
    { label: 'Skipped', value: skipped, type: 'number', status: skipped > 0 ? 'warning' : 'neutral' },
    { label: 'Total', value: total, type: 'number', status: 'neutral' },
  ];

  if (failureDetails.length > 0) {
    details.push({ label: 'First failure', value: failureDetails[0]!, type: 'text', status: 'error' });
  }

  return {
    type: 'test-results',
    headline,
    status,
    details,
    rawOutput: output,
    duration,
    actionSuggestion: failed > 0 ? 'Review the failing tests and fix the issues, then run tests again.' : undefined,
    showRaw: false,
  };
}


// ═══ §5 BUILD OUTPUT DIGESTION ═══

function digestBuildOutput(output: string, tool?: string): OutputDigest {
  const duration = extractDuration(output);
  const lines = output.split('\n');

  let errorCount = 0;
  let warningCount = 0;
  const errors: string[] = [];
  const warnings: string[] = [];

  for (const line of lines) {
    if (/error(?!\s*\d+\s*warning)/i.test(line) && !/0 error/i.test(line)) {
      errorCount++;
      if (errors.length < 5) errors.push(line.trim());
    }
    if (/warning/i.test(line) && !/0 warning/i.test(line)) {
      warningCount++;
      if (warnings.length < 3) warnings.push(line.trim());
    }
  }

  // Detect explicit error/warning counts
  const errCountMatch = output.match(/(\d+)\s+error/i);
  const warnCountMatch = output.match(/(\d+)\s+warning/i);
  if (errCountMatch) errorCount = Math.max(errorCount, parseInt(errCountMatch[1]!, 10));
  if (warnCountMatch) warningCount = Math.max(warningCount, parseInt(warnCountMatch[1]!, 10));

  // Detect bundle size (webpack/vite)
  const sizeMatch = output.match(/(\d+(?:\.\d+)?)\s*(?:kB|KB|MB|bytes)/i);
  const bundleSize = sizeMatch ? sizeMatch[0] : undefined;

  // Detect success patterns
  const isSuccess = errorCount === 0 && (
    /✓|compiled|built|success|done|complete|emitted/i.test(output)
    || /Build succeeded/i.test(output)
    || output.includes('built in')
  );

  const status = errorCount > 0 ? 'error' : (warningCount > 0 ? 'warning' : 'success');
  const headline = errorCount > 0
    ? `Build failed with ${errorCount} error${errorCount === 1 ? '' : 's'}`
    : warningCount > 0
      ? `Build succeeded with ${warningCount} warning${warningCount === 1 ? '' : 's'}`
      : 'Build succeeded';

  const details: DigestDetail[] = [];
  if (errorCount > 0) details.push({ label: 'Errors', value: errorCount, type: 'number', status: 'error' });
  if (warningCount > 0) details.push({ label: 'Warnings', value: warningCount, type: 'number', status: 'warning' });
  if (bundleSize) details.push({ label: 'Bundle size', value: bundleSize, type: 'text', status: 'neutral' });
  if (duration) details.push({ label: 'Duration', value: `${(duration / 1000).toFixed(2)}s`, type: 'text', status: 'neutral' });
  if (errors.length > 0) details.push({ label: 'First error', value: errors[0]!, type: 'text', status: 'error' });

  return {
    type: 'build-result',
    headline,
    status,
    details,
    rawOutput: output,
    duration,
    actionSuggestion: errorCount > 0 ? 'Fix the errors shown above and rebuild.' : undefined,
    showRaw: false,
  };
}


// ═══ §6 INSTALL OUTPUT DIGESTION ═══

function digestInstallOutput(output: string, manager?: string): OutputDigest {
  const duration = extractDuration(output);

  // npm: "added 150 packages, removed 3 packages, ... in 12s"
  const npmAdded = output.match(/added\s+(\d+)\s+package/i);
  const npmRemoved = output.match(/removed\s+(\d+)\s+package/i);
  const npmAudited = output.match(/audited\s+(\d+)\s+package/i);
  const npmVulns = output.match(/(\d+)\s+vulnerabilit/i);
  const npmHighVulns = output.match(/(\d+)\s+high/i);
  const npmCriticalVulns = output.match(/(\d+)\s+critical/i);

  const added = npmAdded ? parseInt(npmAdded[1]!, 10) : 0;
  const removed = npmRemoved ? parseInt(npmRemoved[1]!, 10) : 0;
  const audited = npmAudited ? parseInt(npmAudited[1]!, 10) : 0;
  const vulns = npmVulns ? parseInt(npmVulns[1]!, 10) : 0;

  // pip: "Successfully installed pkg1 pkg2 ..."
  const pipInstalled = output.match(/Successfully installed\s+(.+)/i);
  const pipPackages = pipInstalled ? pipInstalled[1]!.split(/\s+/).length : 0;

  // cargo: "Compiling X packages"
  const cargoCompiled = output.match(/Compiling\s+(\d+)\s+package/i);

  const totalAdded = added || pipPackages || (cargoCompiled ? parseInt(cargoCompiled[1]!, 10) : 0);

  const hasVulns = vulns > 0;
  const hasCritical = (npmCriticalVulns ? parseInt(npmCriticalVulns[1]!, 10) : 0) > 0;
  const status = hasCritical ? 'error' : hasVulns ? 'warning' : 'success';

  const headline = totalAdded > 0
    ? `Installed ${totalAdded} package${totalAdded === 1 ? '' : 's'}${removed > 0 ? `, removed ${removed}` : ''}`
    : 'Installation complete';

  const details: DigestDetail[] = [];
  if (totalAdded > 0) details.push({ label: 'Added', value: totalAdded, type: 'number', status: 'success' });
  if (removed > 0) details.push({ label: 'Removed', value: removed, type: 'number', status: 'neutral' });
  if (audited > 0) details.push({ label: 'Audited', value: audited, type: 'number', status: 'neutral' });
  if (vulns > 0) details.push({ label: 'Vulnerabilities', value: vulns, type: 'number', status: hasCritical ? 'error' : 'warning' });

  return {
    type: 'install-result',
    headline,
    status,
    details,
    rawOutput: output,
    duration,
    actionSuggestion: hasVulns ? 'Run "npm audit fix" to attempt automatic fixes for vulnerabilities.' : undefined,
    showRaw: false,
  };
}


// ═══ §7 GIT OUTPUT DIGESTION ═══

function digestGitStatusOutput(output: string): OutputDigest {
  const lines = output.split('\n').filter((l) => l.trim());
  let staged = 0, modified = 0, untracked = 0, deleted = 0, renamed = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (/^M\s|^MM\s|^AM\s/.test(line) || /modified:/.test(trimmed)) modified++;
    else if (/^A\s/.test(line) || /new file:/.test(trimmed)) staged++;
    else if (/^D\s/.test(line) || /deleted:/.test(trimmed)) deleted++;
    else if (/^R\s/.test(line) || /renamed:/.test(trimmed)) renamed++;
    else if (/^\?\?/.test(line) || /Untracked files:/i.test(trimmed)) untracked++;

    // "Changes to be committed" section entries
    if (/^\s+(modified|new file|deleted|renamed):/.test(line)) staged++;
    // "Changes not staged" section entries count as modified
    if (/^\s+modified:/.test(line) && !output.includes('Changes to be committed')) modified++;
  }

  // Fallback: count lines starting with status codes
  if (staged + modified + untracked + deleted + renamed === 0) {
    for (const line of lines) {
      if (/^[MADRCU?!]/.test(line)) {
        if (line.startsWith('?')) untracked++;
        else if (line.startsWith('M') || line.startsWith('U')) modified++;
        else if (line.startsWith('A')) staged++;
        else if (line.startsWith('D')) deleted++;
        else if (line.startsWith('R')) renamed++;
      }
    }
  }

  const totalChanges = staged + modified + untracked + deleted + renamed;
  const isClean = totalChanges === 0 || /nothing to commit|working tree clean/i.test(output);

  const headline = isClean
    ? 'Working tree is clean — no changes'
    : `${totalChanges} file${totalChanges === 1 ? '' : 's'} changed`;

  const details: DigestDetail[] = [];
  if (staged > 0) details.push({ label: 'Staged', value: staged, type: 'number', status: 'success' });
  if (modified > 0) details.push({ label: 'Modified', value: modified, type: 'number', status: 'warning' });
  if (untracked > 0) details.push({ label: 'Untracked', value: untracked, type: 'number', status: 'neutral' });
  if (deleted > 0) details.push({ label: 'Deleted', value: deleted, type: 'number', status: 'error' });
  if (renamed > 0) details.push({ label: 'Renamed', value: renamed, type: 'number', status: 'neutral' });

  // Detect branch info
  const branchMatch = output.match(/On branch\s+(\S+)/i) ?? output.match(/^##\s+(\S+)/m);
  if (branchMatch) details.push({ label: 'Branch', value: branchMatch[1]!, type: 'badge', status: 'neutral' });

  return {
    type: 'git-status',
    headline,
    status: isClean ? 'success' : 'info',
    details,
    rawOutput: output,
    actionSuggestion: modified > 0 || untracked > 0 ? 'Use "git add" to stage changes, then "git commit" to save.' : undefined,
    showRaw: false,
  };
}

function digestGitLogOutput(output: string): OutputDigest {
  const lines = output.split('\n').filter((l) => l.trim());
  const commits: Array<{ hash: string; message: string }> = [];

  for (const line of lines) {
    // "abc1234 Commit message here"
    const match = line.match(/^([0-9a-f]{7,40})\s+(.+)/i);
    if (match) {
      commits.push({ hash: match[1]!, message: match[2]! });
    }
  }

  const headline = commits.length > 0
    ? `${commits.length} recent commit${commits.length === 1 ? '' : 's'}`
    : 'No commits found';

  const details: DigestDetail[] = [
    { label: 'Total shown', value: commits.length, type: 'number', status: 'neutral' },
  ];
  // Show last 5 commit messages
  for (let i = 0; i < Math.min(commits.length, 5); i++) {
    const c = commits[i]!;
    details.push({ label: c.hash.slice(0, 7), value: c.message, type: 'text', status: 'neutral' });
  }

  return {
    type: 'git-log',
    headline,
    status: 'info',
    details,
    rawOutput: output,
    showRaw: false,
  };
}

function digestGitDiffOutput(output: string): OutputDigest {
  const lines = output.split('\n');
  let additions = 0, deletions = 0;
  const fileChanges: Array<{ file: string; added: number; removed: number }> = [];

  // Stat-style: " file.ts | 5 ++-"
  for (const line of lines) {
    const statMatch = line.match(/^\s*(.+?)\s*\|\s*(\d+)\s*([+-]*)/);
    if (statMatch) {
      const file = statMatch[1]!.trim();
      const plusCount = (statMatch[3] ?? '').split('').filter((c) => c === '+').length;
      const minusCount = (statMatch[3] ?? '').split('').filter((c) => c === '-').length;
      const total = parseInt(statMatch[2]!, 10);
      const ratio = plusCount + minusCount > 0 ? plusCount / (plusCount + minusCount) : 0.5;
      const added = Math.round(total * ratio);
      const removed = total - added;
      fileChanges.push({ file, added, removed });
      additions += added;
      deletions += removed;
    }
  }

  // Summary line: "3 files changed, 45 insertions(+), 12 deletions(-)"
  const summaryMatch = output.match(/(\d+)\s+file.*?(\d+)\s+insertion.*?(\d+)\s+deletion/i);
  if (summaryMatch) {
    additions = parseInt(summaryMatch[2]!, 10);
    deletions = parseInt(summaryMatch[3]!, 10);
  }

  // Diff-style counting: lines starting with + or -
  if (additions === 0 && deletions === 0) {
    for (const line of lines) {
      if (/^\+[^+]/.test(line)) additions++;
      else if (/^-[^-]/.test(line)) deletions++;
    }
  }

  const filesChanged = fileChanges.length || (summaryMatch ? parseInt(summaryMatch[1]!, 10) : 0);
  const headline = filesChanged > 0
    ? `${filesChanged} file${filesChanged === 1 ? '' : 's'} changed: +${additions} -${deletions}`
    : `+${additions} -${deletions} lines`;

  const details: DigestDetail[] = [
    { label: 'Files changed', value: filesChanged, type: 'number', status: 'neutral' },
    { label: 'Additions', value: additions, type: 'number', status: 'success' },
    { label: 'Deletions', value: deletions, type: 'number', status: 'error' },
  ];

  for (const fc of fileChanges.slice(0, 8)) {
    details.push({ label: fc.file, value: `+${fc.added} -${fc.removed}`, type: 'text', status: 'neutral' });
  }

  return {
    type: 'git-diff',
    headline,
    status: 'info',
    details,
    rawOutput: output,
    showRaw: false,
  };
}


// ═══ §8 ERROR DIGESTION ═══

function digestErrorOutput(command: string, output: string, exitCode: number): OutputDigest {
  const lines = output.split('\n');
  let errorLine = '';
  let suggestion = '';

  // Find the most informative error line
  for (const line of lines) {
    const trimmed = line.trim();
    if (/error|Error|ERROR|fatal|FATAL|panic/i.test(trimmed) && !errorLine) {
      errorLine = trimmed;
    }
  }

  // If no explicit error line, use last non-empty line
  if (!errorLine) {
    for (let i = lines.length - 1; i >= 0; i--) {
      if (lines[i]!.trim()) { errorLine = lines[i]!.trim(); break; }
    }
  }

  // Common error pattern suggestions
  if (/command not found/i.test(output)) {
    suggestion = `The command "${command.split(/\s+/)[0]}" is not installed. Try installing it first.`;
  } else if (/permission denied/i.test(output)) {
    suggestion = 'You may not have permission. Try running with "sudo" or check file permissions.';
  } else if (/ENOENT|no such file|not found/i.test(output)) {
    suggestion = 'A file or directory was not found. Check the path and spelling.';
  } else if (/EADDRINUSE|address already in use/i.test(output)) {
    const portMatch = output.match(/port\s*(\d+)|:(\d+)/i);
    const port = portMatch ? (portMatch[1] ?? portMatch[2]) : 'the port';
    suggestion = `Port ${port} is already in use. Stop the other process or use a different port.`;
  } else if (/ECONNREFUSED|connection refused/i.test(output)) {
    suggestion = 'Could not connect to the server. Make sure it is running.';
  } else if (/out of memory|heap|ENOMEM/i.test(output)) {
    suggestion = 'The process ran out of memory. Try closing other applications or increasing the memory limit.';
  } else if (/syntax error|SyntaxError|unexpected token/i.test(output)) {
    suggestion = 'There is a syntax error in the code. Check the file and line number mentioned above.';
  } else if (/MODULE_NOT_FOUND|Cannot find module/i.test(output)) {
    suggestion = 'A required module is missing. Try running "npm install" to install dependencies.';
  } else if (/ETIMEDOUT|timeout/i.test(output)) {
    suggestion = 'The operation timed out. Check your network connection or try again.';
  }

  const details: DigestDetail[] = [
    { label: 'Command', value: command, type: 'text', status: 'neutral' },
    { label: 'Exit code', value: exitCode, type: 'number', status: 'error' },
  ];
  if (errorLine) details.push({ label: 'Error', value: errorLine.slice(0, 200), type: 'text', status: 'error' });

  return {
    type: 'error-summary',
    headline: errorLine ? errorLine.slice(0, 80) : `Command failed with exit code ${exitCode}`,
    status: 'error',
    details,
    rawOutput: output,
    actionSuggestion: suggestion || 'Review the error message and try again.',
    showRaw: false,
  };
}


// ═══ §9 GENERIC DIGESTION ═══

function digestGenericSuccess(command: string, output: string): OutputDigest {
  const lineCount = output.split('\n').filter((l) => l.trim()).length;
  return {
    type: 'general-success',
    headline: `Command completed successfully`,
    status: 'success',
    details: [
      { label: 'Command', value: command, type: 'text', status: 'neutral' },
      { label: 'Output lines', value: lineCount, type: 'number', status: 'neutral' },
    ],
    rawOutput: output,
    showRaw: lineCount <= 5,
  };
}


// ═══ §10 DIGEST TYPE DETECTION ═══

const COMMAND_TYPE_MAP: ReadonlyArray<{ pattern: RegExp; type: DigestType }> = [
  { pattern: /^(npm\s+test|npx\s+(jest|vitest|mocha)|pytest|cargo\s+test|go\s+test)\b/, type: 'test-results' },
  { pattern: /^(npm\s+run\s+(build|compile)|cargo\s+build|go\s+build|npx\s+tsc|webpack|vite\s+build)\b/, type: 'build-result' },
  { pattern: /^(npm\s+(install|i|ci|add)|yarn\s+(install|add)|pnpm\s+(install|add|i)|pip\s+install|cargo\s+(add|install))\b/, type: 'install-result' },
  { pattern: /^git\s+status\b/, type: 'git-status' },
  { pattern: /^git\s+diff\b/, type: 'git-diff' },
  { pattern: /^git\s+log\b/, type: 'git-log' },
  { pattern: /^(ls|dir|find\s+\.\s+-maxdepth)\b/, type: 'file-listing' },
  { pattern: /^(ps|top|htop)\b/, type: 'process-list' },
  { pattern: /^(du|df)\b/, type: 'disk-usage' },
  { pattern: /^(deploy|firebase\s+deploy|vercel|netlify\s+deploy|fly\s+deploy)\b/, type: 'deploy-result' },
];

function detectDigestType(command: string, output: string): DigestType {
  const trimmedCmd = command.trim();

  for (const { pattern, type } of COMMAND_TYPE_MAP) {
    if (pattern.test(trimmedCmd)) return type;
  }

  // Output-based detection fallback
  if (/Tests:|test result:|passed|failed/i.test(output) && /\d+\s+(passed|failed)/i.test(output)) {
    return 'test-results';
  }
  if (/compiled|built|bundle|webpack|vite/i.test(output) && /error|warning|success/i.test(output)) {
    return 'build-result';
  }
  if (/added\s+\d+\s+package|Successfully installed/i.test(output)) {
    return 'install-result';
  }

  return 'raw-output';
}


// ═══ §11 MAIN DIGEST FUNCTION ═══

function digestOutput(command: string, output: string, exitCode: number): OutputDigest {
  // If the command failed with a non-zero exit code, try error digestion
  if (exitCode !== 0) {
    const dtype = detectDigestType(command, output);
    // For recognized commands, use specific digesters first (they handle errors too)
    switch (dtype) {
      case 'test-results': return digestTestOutput(output);
      case 'build-result': return digestBuildOutput(output);
      case 'install-result': return digestInstallOutput(output);
      default: return digestErrorOutput(command, output, exitCode);
    }
  }

  const dtype = detectDigestType(command, output);

  switch (dtype) {
    case 'test-results': return digestTestOutput(output);
    case 'build-result': return digestBuildOutput(output);
    case 'install-result': return digestInstallOutput(output);
    case 'git-status': return digestGitStatusOutput(output);
    case 'git-diff': return digestGitDiffOutput(output);
    case 'git-log': return digestGitLogOutput(output);
    case 'file-listing': return digestFileListing(output);
    case 'process-list': return digestProcessList(output);
    case 'disk-usage': return digestDiskUsage(output);
    case 'deploy-result': return digestDeployResult(output);
    default: return digestGenericSuccess(command, output);
  }
}


// ═══ §12 AUXILIARY DIGESTERS ═══

function digestFileListing(output: string): OutputDigest {
  const lines = output.split('\n').filter((l) => l.trim());
  let fileCount = 0, dirCount = 0;

  for (const line of lines) {
    if (/^d/i.test(line) || line.endsWith('/')) dirCount++;
    else if (/^-|^[lbcps]/.test(line)) fileCount++;
    else fileCount++; // fallback: count as file
  }

  // If ls -la, the first line is "total X" — skip it
  if (lines.length > 0 && /^total\s+\d+/i.test(lines[0]!)) {
    fileCount = Math.max(0, fileCount - 1);
  }

  return {
    type: 'file-listing',
    headline: `${fileCount} file${fileCount === 1 ? '' : 's'}, ${dirCount} folder${dirCount === 1 ? '' : 's'}`,
    status: 'info',
    details: [
      { label: 'Files', value: fileCount, type: 'number', status: 'neutral' },
      { label: 'Directories', value: dirCount, type: 'number', status: 'neutral' },
    ],
    rawOutput: output,
    showRaw: true,
  };
}

function digestProcessList(output: string): OutputDigest {
  const lines = output.split('\n').filter((l) => l.trim());
  const processCount = Math.max(0, lines.length - 1); // subtract header

  return {
    type: 'process-list',
    headline: `${processCount} process${processCount === 1 ? '' : 'es'} running`,
    status: 'info',
    details: [
      { label: 'Total processes', value: processCount, type: 'number', status: 'neutral' },
    ],
    rawOutput: output,
    showRaw: true,
  };
}

function digestDiskUsage(output: string): OutputDigest {
  const lines = output.split('\n').filter((l) => l.trim());
  const entries: Array<{ name: string; size: string }> = [];

  for (const line of lines) {
    const match = line.match(/^([\d.]+[KMGTP]?)\s+(.+)/i)
      ?? line.match(/^(\S+)\s+\S+\s+\S+\s+(\S+)\s+\S+\s+(.+)/); // df format

    if (match) {
      entries.push({ size: match[1]!, name: (match[3] ?? match[2] ?? '').trim() });
    }
  }

  return {
    type: 'disk-usage',
    headline: `${entries.length} entr${entries.length === 1 ? 'y' : 'ies'} listed`,
    status: 'info',
    details: entries.slice(0, 10).map((e) => ({
      label: e.name,
      value: e.size,
      type: 'text' as const,
      status: 'neutral' as const,
    })),
    rawOutput: output,
    showRaw: true,
  };
}

function digestDeployResult(output: string): OutputDigest {
  const urlMatch = output.match(/https?:\/\/[^\s'"]+/);
  const isSuccess = /success|deployed|live|ready|published/i.test(output);
  const isFailed = /fail|error|abort/i.test(output);

  const headline = isFailed
    ? 'Deployment failed'
    : isSuccess
      ? 'Deployment succeeded'
      : 'Deployment completed';

  const details: DigestDetail[] = [];
  if (urlMatch) details.push({ label: 'URL', value: urlMatch[0], type: 'link', status: 'success' });

  return {
    type: 'deploy-result',
    headline,
    status: isFailed ? 'error' : 'success',
    details,
    rawOutput: output,
    actionSuggestion: isFailed ? 'Check the deployment logs for details.' : undefined,
    showRaw: false,
  };
}


// ═══ §13 SHOULD DIGEST & SUMMARY ═══

function shouldDigest(command: string, state: DigestState): boolean {
  if (!state.enabled) return false;
  if (state.mode === 'never') return false;
  if (state.mode === 'always') return true;

  // Auto mode: digest only recognized commands
  const dtype = detectDigestType(command, '');
  return dtype !== 'raw-output';
}

function getDigestSummary(state: DigestState): {
  total: number;
  byType: Record<DigestType, number>;
  byStatus: Record<string, number>;
} {
  const byType = {} as Record<DigestType, number>;
  const byStatus = {} as Record<string, number>;

  const allTypes: DigestType[] = [
    'test-results', 'build-result', 'install-result', 'error-summary',
    'git-status', 'git-diff', 'git-log', 'file-listing', 'process-list',
    'disk-usage', 'deploy-result', 'general-success', 'general-error', 'raw-output',
  ];
  for (const t of allTypes) byType[t] = 0;
  const allStatuses = ['success', 'warning', 'error', 'info', 'neutral'];
  for (const s of allStatuses) byStatus[s] = 0;

  for (const digest of state.digests) {
    byType[digest.type] = (byType[digest.type] ?? 0) + 1;
    byStatus[digest.status] = (byStatus[digest.status] ?? 0) + 1;
  }

  return {
    total: state.digests.length,
    byType,
    byStatus,
  };
}


// ═══ §14 EXPORTS ═══

export {
  createDigestState,
  enableDigest,
  disableDigest,
  setDigestMode,
  digestOutput,
  digestTestOutput,
  digestBuildOutput,
  digestInstallOutput,
  digestGitStatusOutput,
  digestGitLogOutput,
  digestGitDiffOutput,
  digestErrorOutput,
  digestGenericSuccess,
  detectDigestType,
  addDigest,
  clearDigests,
  toggleRaw,
  getDigests,
  getDigestSummary,
  shouldDigest,
};
